// src/pages/Auth/AdminOtpVerificationPage.jsx
import React, { useRef, useState, useEffect } from "react";
import logo from "../../assets/Images/Frame 45.png";
import { Form, Button, notification } from "antd";
import { verifyAdminOtp, resendAdminOtp } from "../../Services/auth";
import styled from "styled-components";
import COLORS from "../../common/Colors";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { useDispatch } from "react-redux";
import { setIsLoggedIn, setUserInfo } from "../../Redux/AuthSlice";
import { ArrowLeftOutlined } from "@ant-design/icons";

// ✅ Styled Components
const DivContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(90deg, #3c4173 0%, #428dc7 100%);
`;

const StyledContainer = styled.div`
  width: 400px;
  background: #fff;
  border-radius: 10px;
  padding: 24px;
  box-shadow: 0px 3px 6px #00000029;
`;

const StyledCenterAlign = styled.h3`
  text-align: center;
  font-size: 1.4rem;
  padding-bottom: 10px;
  font-weight: 600;
`;

const StyledCenterAlignLogo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
`;

const OtpInputGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 6px;
`;

const OtpInput = styled.input`
  width: 48px;
  height: 60px;
  font-size: 1.5rem;
  text-align: center;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  outline: none;
  background: #f9f9f9;
  transition: all 0.2s;
  &:focus {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const StyleSubmitButton = styled(Button)`
  background-color: ${COLORS.primary};
  color: white;
  height: 40px;
  border-radius: 8px;
  font-weight: 600;
  &:hover {
    background-color: ${COLORS.primaryHover || "#1761a0"};
    color: white;
  }
`;

function AdminOtpVerificationPage() {
  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef([]);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(10);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [attemptMessage, setAttemptMessage] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const tempToken = location.state?.temp_token;

  useEffect(() => {
    if (!tempToken) {
      notification.error({
        message: "Authentication Error",
        description: "Missing temporary token. Please login again.",
        duration: 3,
      });
      navigate("/login");
    }
  }, [tempToken, navigate]);

  useEffect(() => {
    let timer;
    if (resendDisabled && resendTimer > 0) {
      timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
    } else if (resendTimer === 0) {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [resendTimer, resendDisabled]);

  const handleInput = (e, index) => {
    const input = e.target.value.replace(/\D/g, "");
    if (!input) return;

    const updatedOtp = [...otp];
    let currentIndex = index;

    for (let i = 0; i < input.length && currentIndex < OTP_LENGTH; i++) {
      updatedOtp[currentIndex] = input[i];
      currentIndex++;
    }

    setOtp(updatedOtp);

    if (currentIndex < OTP_LENGTH) {
      inputRefs.current[currentIndex]?.focus();
    } else {
      inputRefs.current[OTP_LENGTH - 1]?.blur();
    }
  };

  const handleKeyDown = (e, index) => {
    const updated = [...otp];

    if (e.key === "Backspace") {
      if (updated[index]) {
        updated[index] = "";
        setOtp(updated);
      } else if (index > 0) {
        updated[index - 1] = "";
        setOtp(updated);
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (e.key === "Enter" && otp.every((d) => d !== "")) {
      submitOtp();
    }
  };

  const submitOtp = async () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== OTP_LENGTH || !/^\d+$/.test(enteredOtp)) {
      notification.error({
        message: "Invalid OTP",
        description: `Please enter a valid ${OTP_LENGTH}-digit numeric code.`,
        duration: 3,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await verifyAdminOtp(enteredOtp, tempToken);
        
      if (response && response.status_code === 200) {
        const expireTime = new Date(response.data.expire_at).getTime();
        const now = new Date().getTime();
        const expiresInMinutes = (expireTime - now) / (1000 * 60);
        const isHttps = window.location.protocol === "https:";

        Cookies.set("authToken", response.data.token, {
          expires: expiresInMinutes / (60 * 24),
          secure: isHttps,
          sameSite: isHttps ? "None" : "Lax",
        });

        Cookies.set("isLoggedIn", "true", {
          expires: expiresInMinutes / (60 * 24),
          secure: isHttps,
          sameSite: isHttps ? "None" : "Lax",
        });

        dispatch(setIsLoggedIn(true));
        dispatch(setUserInfo({ name: response.data?.name }));

        navigate("/dashboard");
      } else {
        notification.error({
          message: "OTP verification failed",
          description:
            response?.message || "Please check the code and try again",
          duration: 3,
        });
        if(response?.attempt_message){
          setAttemptMessage(response.attempt_message);
          // setTimeout(() => {
          //   setAttemptMessage("");
          // }, 5000);
        }

        if (response && response.status_code === 402) {
          // redirect to login
          setTimeout(() => {
              navigate("/login");
          }, 4000);
        }
      }
    } catch (error) {
      console.error("OTP Verify failed:", error);
      notification.error({
        message: "OTP verification failed",
        description: "Invalid OTP. Please try again",
        duration: 3,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setResendDisabled(true);
      setResendTimer(10);

      const response = await resendAdminOtp(tempToken);
        console.log('response==', response)
      if (response && response.status_code === 200) {
        if(response.data.attempt_message){
          setAttemptMessage(response.data.attempt_message);
          // setTimeout(() => {
          //   setAttemptMessage("");
          // }, 15000);
        }
        notification.success({
          message: "OTP Sent Successfully",
          description: "Please check your email for the new code",
          duration: 3,
        });
      } else {
        notification.error({
          message: "OTP Resend Error",
          description:
            response?.message ||
            "An error occurred while attempting to resend the OTP",
          duration: 3,
        });

        if (response && response.status_code === 402) {
          // redirect to login
          setTimeout(() => {
              navigate("/login");
          }, 4000);
        }
        
      }
    } catch (error) {
      console.error("Resend OTP failed:", error);
      notification.error({
        message: "OTP Resend Error",
        description: "Something went wrong. Please try again",
        duration: 3,
      });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").replace(/\D/g, ""); // only digits

    if (paste.length !== OTP_LENGTH) return;

    const updatedOtp = paste.split("").slice(0, OTP_LENGTH);
    setOtp(updatedOtp);

    // Move focus to last input
    inputRefs.current[OTP_LENGTH - 1]?.focus();
    };

  return (
    <DivContainer>
      <StyledContainer>

        <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/login")}
            style={{ marginBottom: "8px", paddingLeft: 0 }}
        >
            Back
        </Button>

        <StyledCenterAlignLogo>
          <img src={logo} alt="logo" />
        </StyledCenterAlignLogo>

        <StyledCenterAlign>Verify OTP</StyledCenterAlign>

        <Form>
          <div style={{ textAlign: "end" }}>
            <Button
              type="link"
              onClick={handleResendOtp}
              disabled={resendDisabled}
              style={{ padding: 0 }}
            >
              {resendDisabled
                ? `Resend OTP in ${resendTimer}s`
                : "Resend OTP"}
            </Button>
          </div>

          <OtpInputGroup>
            {otp.map((digit, index) => (
              <OtpInput
                key={index}
                value={digit}
                ref={(el) => (inputRefs.current[index] = el)}
                onChange={(e) => handleInput(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onFocus={(e) => e.target.select()}
                onPaste={handlePaste}
                autoComplete="one-time-code"
                maxLength={1}
              />
            ))}
          </OtpInputGroup>
          {attemptMessage && (
            <div style={{textAlign:'start', marginTop: '-10px', marginBottom:'10px', fontSize:'12px'}}><b>Note:</b> {attemptMessage}</div>
          )}
          <StyleSubmitButton
            type="primary"
            block
            onClick={submitOtp}
            loading={loading}
          >
            Verify OTP
          </StyleSubmitButton>
        </Form>
      </StyledContainer>
    </DivContainer>
  );
}

export default AdminOtpVerificationPage;
